import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import ReviewList from './ReviewList';
import ImageGallery from './ImageGallery';
import ReviewDetails from './ReviewDetails';
import ImageModal from './ImageModal';

const ADMIN_AUTH_KEY = 'adminAuthenticated';

/**
 * Page: Review images (admin). Fetches reviews, shows list + gallery + modal.
 * Auth: redirects to /admin if not authenticated.
 */
function ReviewImagesPage() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedReviewId, setSelectedReviewId] = useState(null);
  const [images, setImages] = useState([]);
  const [loadingImages, setLoadingImages] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (localStorage.getItem(ADMIN_AUTH_KEY) !== 'true') {
      navigate('/admin');
      return;
    }
    fetchReviews();
  }, [navigate]);

  const fetchReviews = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/api/ugc/reviews', {
        headers: { 'Content-Type': 'application/json' },
      });
      setReviews(response.data.reviews || []);
    } catch (err) {
      const message = err.response?.data?.message || err.message || 'Nieznany błąd';
      setError(`Błąd podczas pobierania opinii: ${message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchImagesForReview = useCallback(async (reviewId, orderNumber) => {
    try {
      setLoadingImages(true);
      setSelectedReviewId(reviewId);
      setError(null);
      const response = await api.get(`/api/ugc/reviews/${reviewId}/images`, {
        params: { orderNumber: orderNumber ?? '' },
      });
      setImages(response.data.success && response.data.images ? response.data.images : []);
    } catch (err) {
      const message = err.response?.data?.message || err.message || 'Nieznany błąd';
      setError(`Błąd podczas pobierania zdjęć: ${message}`);
      setImages([]);
    } finally {
      setLoadingImages(false);
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem(ADMIN_AUTH_KEY);
    navigate('/admin');
  };

  const openImageModal = (image, index) => {
    setSelectedImage(image);
    setSelectedImageIndex(index);
  };

  const closeImageModal = () => {
    setSelectedImage(null);
    setSelectedImageIndex(0);
  };

  const navigateImage = (direction) => {
    if (!selectedImage) return;
    const newIndex = selectedImageIndex + direction;
    if (newIndex >= 0 && newIndex < images.length) {
      setSelectedImageIndex(newIndex);
      setSelectedImage(images[newIndex]);
    }
  };

  const selectedReview = selectedReviewId
    ? reviews.find((r) => r.id === selectedReviewId)
    : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-8">
        <div className="text-center">
          <div
            className="animate-spin rounded-full h-12 w-12 border-2 border-green-600 border-t-transparent mx-auto"
            aria-hidden
          />
          <p className="mt-4 text-gray-600">Ładowanie opinii...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-7xl">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <header className="flex flex-wrap justify-between items-center gap-4 mb-6">
            <h1 className="text-2xl font-bold text-gray-900">Zdjęcia z opinii</h1>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => navigate('/admin')}
                className="px-4 py-2 rounded-lg font-medium bg-gray-600 text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 min-h-[44px]"
              >
                Powrót do opinii
              </button>
              <button
                type="button"
                onClick={handleLogout}
                className="px-4 py-2 rounded-lg font-medium bg-gray-600 text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 min-h-[44px]"
              >
                Wyloguj
              </button>
            </div>
          </header>

          {error && (
            <div
              className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg"
              role="alert"
            >
              <p className="text-red-800">{error}</p>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ReviewList
              reviews={reviews}
              selectedReviewId={selectedReviewId}
              onSelectReview={fetchImagesForReview}
            />

            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {selectedReviewId
                  ? `Zdjęcia opinii #${selectedReviewId}`
                  : 'Wybierz opinię'}
              </h2>

              {loadingImages ? (
                <div className="text-center py-8">
                  <div
                    className="animate-spin rounded-full h-8 w-8 border-2 border-green-600 border-t-transparent mx-auto"
                    aria-hidden
                  />
                  <p className="mt-2 text-gray-600">Ładowanie zdjęć...</p>
                </div>
              ) : selectedReviewId ? (
                <>
                  <ImageGallery images={images} onOpenModal={openImageModal} />
                  <ReviewDetails review={selectedReview} />
                </>
              ) : (
                <div className="p-8 bg-gray-50 border border-gray-200 rounded-lg text-center">
                  <p className="text-gray-600">
                    Wybierz opinię z listy po lewej, aby zobaczyć zdjęcia
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {selectedImage && (
        <ImageModal
          image={selectedImage}
          imageIndex={selectedImageIndex}
          totalCount={images.length}
          onClose={closeImageModal}
          onPrev={() => navigateImage(-1)}
          onNext={() => navigateImage(1)}
          hasPrev={selectedImageIndex > 0}
          hasNext={selectedImageIndex < images.length - 1}
        />
      )}
    </div>
  );
}

export default ReviewImagesPage;
